import { ReactElement, useMemo } from "react";
import { Hide, Show } from "@chakra-ui/react";

import { OrderModel } from "api";
import BackToTopFAB from "components/BackToTopFAB";
import { ComponentTabProps } from "components/Documents/documentsTabs/documentsTabPanels/DocumentsPageTabs";
import DocumentsEmptyState from "components/DocumentsEmptyState";
import FetchMoreWhenInView from "components/FetchMoreWhenInView";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DOCUMENTS_BACK_TO_TOP_MIN_LINES,
  MobileSortableCardTable,
  SortableCardTable,
} from "components/SortableTable";
import DateColumn from "components/tableColumns/DateColumn/DateColumn";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import useDocumentOrders from "hooks/api/documents/useDocumentOrders";
import useOrderFiles from "hooks/api/orders/useOrderFiles";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { SHIPPING_STATUS } from "services/OrderService";
import { MOBILE_BREAKPOINT_SIZE } from "util/styleHelpers";

export const ordersColumns: ColumnDefinition<
  OrderModel & {
    ShippingStatus: string;
    "DocumentLines.WarehouseName": "string";
  }
>[] = [
  {
    property: "releaseNumber",
    sortKey: "ReleaseNumber",
    columnLabel: "Release #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "createDate",
    sortKey: "CreateDateTime",
    columnLabel: "Create Date",
    width: "11ch",
    maxWidth: "11ch",
  },
  {
    property: "contractNumber",
    sortKey: "ContractNumber",
    columnLabel: "Contract #",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "poNumber",
    sortKey: "NumAtCard",
    columnLabel: "PO #",
    width: "10ch",
    maxWidth: "13ch",
  },
  {
    property: "productName",
    sortKey: "SearchItemShortName",
    columnLabel: "Product",
    width: "15ch",
    maxWidth: "15ch",
  },
  {
    property: "quantityFormatted",
    sortKey: "SearchQuantity",
    columnLabel: "Qty",
    width: "10ch",
    maxWidth: "10ch",
    alignRight: true,
    alwaysFullWidth: true,
  },
  {
    property: "shipFrom",
    sortKey: "SearchWarehouseCode",
    columnLabel: "Ship From",
    width: "10ch",
    maxWidth: "10ch",
  },
  {
    property: "shipTo",
    sortKey: "ShipToCode",
    columnLabel: "Ship To",
    width: "15ch",
    maxWidth: "15ch",
  },
  {
    property: "mode",
    sortKey: "ModeOfTransportationCode",
    columnLabel: "Mode",
    width: "5ch",
    maxWidth: "5ch",
  },
  {
    property: "salesTerms",
    sortKey: "SalesTermsCode",
    columnLabel: "Sales Terms",
    width: "5ch",
    maxWidth: "5ch",
  },
  {
    property: "shippingStatus",
    columnLabel: "Status",
    width: "12ch",
    maxWidth: "12ch",
  },
];

const DocumentsOrdersTable = ({ search }: ComponentTabProps): ReactElement => {
  const selectedCustomerCodes = useSelectedCustomerCode();
  const { filters, activeSort } = useRequestParams();

  const {
    data,
    isLoading: ordersLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDocumentOrders(activeSort as ActiveSort<OrderModel>, {
    cardCodes: selectedCustomerCodes,
    searchString: search,
    ...filters,
  });

  const documentOrders = useMemo(
    () => data?.pages?.map(({ orders: _orders }) => _orders).flat() ?? [],
    [data]
  );

  const { mutateAsync } = useOrderFiles();

  const ordersTableEntries = useMemo(
    () =>
      documentOrders?.map((value) => ({
        ...value,
        releaseNumber:
          value.shippingStatus === SHIPPING_STATUS.NOT_SHIPPED ? (
            value.releaseNumber
          ) : (
            <FileDownloadLink
              label={value.releaseNumber}
              onFetchFiles={mutateAsync}
              docEntry={value.docEntry}
            />
          ),
        createDate: <DateColumn date={value.createDate} />,
      })) ?? [],
    [documentOrders, mutateAsync]
  );

  if (ordersLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {documentOrders && documentOrders.length > 0 ? (
        <>
          <BackToTopFAB
            isVisible={
              ordersTableEntries.length > DOCUMENTS_BACK_TO_TOP_MIN_LINES
            }
          >
            <Hide below={MOBILE_BREAKPOINT_SIZE}>
              <SortableCardTable
                values={ordersTableEntries}
                columns={ordersColumns}
              />
            </Hide>
            <Show below={MOBILE_BREAKPOINT_SIZE}>
              <MobileSortableCardTable
                values={ordersTableEntries ?? []}
                columns={ordersColumns}
              />
            </Show>
            <FetchMoreWhenInView
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              key={`${data?.pages.length}`}
            />
          </BackToTopFAB>
        </>
      ) : (
        <DocumentsEmptyState />
      )}
    </>
  );
};

export default DocumentsOrdersTable;
